import { DetailsPanel, ImageUpload, Prompt, SnackbarStatus, useSnackbar } from '@weareopenr/component-library';
import { IMenuItem } from 'src/types/menu.types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { QueryKeys } from 'src/hooks/QueryKeys';
import { updateGlobalItemDetails } from 'src/api/updateGlobalItemDetails';
import { useState } from 'react';

export interface ProductImagePanelProps {
  currentItem: IMenuItem;
}

interface UpdateImageVariables {
  currentItem: IMenuItem;
  image: File | null;
}

const useUpdateImage = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async ({ currentItem, image }: UpdateImageVariables) => {
      const updatePayload = {
        files: image,
      };

      await updateGlobalItemDetails(currentItem.itemId, updatePayload);
    },
    onMutate: async ({ currentItem, image }) => {
      const queryKey = [QueryKeys.item, currentItem.itemId];

      await queryClient.cancelQueries({ queryKey });

      const localImageUrl = image ? URL.createObjectURL(image) : null;
      queryClient.setQueryData(queryKey, { ...currentItem, image: localImageUrl });
    },
    onError: () => {
      showSnackbar({
        status: SnackbarStatus.ERROR,
        message: 'Something went wrong whilst updating the product image',
      });
    },
    onSettled: (data, error, { currentItem }) => {
      const queryKey = [QueryKeys.item, currentItem.itemId];

      queryClient.invalidateQueries(queryKey);
    },
  });
};

const ProductImagePanel = ({ currentItem }: ProductImagePanelProps) => {
  const [isRemovePromptVisible, setIsRemovePromptVisible] = useState(false);
  const { mutate } = useUpdateImage();

  const onImageChange = async (image: File | null) => {
    if (image === null) {
      setIsRemovePromptVisible(true);
    } else {
      mutate({ currentItem, image });
    }
  };

  const onConfirmDelete = () => {
    mutate({ currentItem, image: null });
    setIsRemovePromptVisible(false);
  };

  return (
    <DetailsPanel title="Product Image">
      <div>
        <ImageUpload
          prompt="Image"
          altText={`Product image of ${currentItem.name}`}
          image={currentItem.image}
          setImage={onImageChange}
          shouldDeleteImage={true}
        />
      </div>
      <Prompt
        promptHeading={'Delete image?'}
        closePrompt={() => setIsRemovePromptVisible(false)}
        open={isRemovePromptVisible}
        buttonHandler={onConfirmDelete}
        buttonText="Delete"
        buttonColour="tomato"
        backButton={true}
        size="small"
      >
        This action cannot be undone.
      </Prompt>
    </DetailsPanel>
  );
};

export default ProductImagePanel;
